import { useState, useCallback } from "react";
import type { FloorConfig, ConfidenceThresholds } from "../../verification/state/floor-resolution";

interface FloorConfigEditorProps {
  thresholds: ConfidenceThresholds;
  onChange: (thresholds: ConfidenceThresholds) => void;
}

const COMMON_FIELD_TYPES = [
  "currency",
  "date",
  "text",
  "number",
  "phone",
  "email",
];

/**
 * R-126 (2026-05-23) — commit-on-blur input.
 *
 * Before: every spinner click / keystroke called `onChange` directly,
 * which (via the parent's `handleSave`) issued a PATCH and previously
 * also a `refresh({ bypassCache: true })` that flipped `isLoading=true`
 * mid-edit — the editor remounted, focus was lost, and a single
 * 0.85→0.90 adjustment churned through three save+reload cycles. The
 * parent's reload is gone now; this input also locally debounces the
 * commit so a multi-step adjustment (clicking the up-arrow three times)
 * is a single PATCH on blur instead of three.
 *
 * The input keeps a draft string so partial values like "0." while
 * typing don't immediately fire `onCommit` with `NaN`. The committed
 * value is the parsed float, clamped to the component's [min, max].
 */
function NumberInputOnBlur({
  value,
  onCommit,
  min,
  max,
  step,
  ariaLabel,
}: {
  value: number;
  onCommit: (next: number) => void;
  min: number;
  max: number;
  step: number;
  ariaLabel?: string;
}) {
  const [draft, setDraft] = useState<string>(value.toString());
  const [focused, setFocused] = useState(false);

  // When the canonical value changes from outside (e.g. a successful save
  // round-trip with a clamped value), sync the draft — but only when the
  // input isn't focused, so we don't clobber the in-flight edit.
  if (!focused && draft !== value.toString()) {
    // Idiomatic state-derived-from-props in React 18: setState inside the
    // render body is the documented pattern when the source is changing.
    // (cf. https://react.dev/reference/react/useState#storing-information-from-previous-renders)
    setDraft(value.toString());
  }

  const commit = (raw: string) => {
    const parsed = parseFloat(raw);
    if (Number.isNaN(parsed)) {
      // Bad input — revert to the canonical value.
      setDraft(value.toString());
      return;
    }
    const clamped = Math.min(max, Math.max(min, parsed));
    if (clamped !== value) onCommit(clamped);
    setDraft(clamped.toString());
  };

  return (
    <input
      type="number"
      min={min}
      max={max}
      step={step}
      value={draft}
      aria-label={ariaLabel}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        setFocused(false);
        commit(e.target.value);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

export function FloorConfigEditor({
  thresholds,
  onChange,
}: FloorConfigEditorProps) {
  const floors = thresholds.autoSkipFloors ?? {};
  const [newFieldType, setNewFieldType] = useState("");

  const updateFloor = useCallback(
    (fieldType: string, updates: Partial<FloorConfig>) => {
      const current = floors[fieldType] ?? {
        floor: 0.95,
        mode: "fixed" as const,
        hardMinimum: 0.88,
        initialFloor: 0.95,
      };
      const updated = { ...current, ...updates };
      onChange({
        ...thresholds,
        autoSkipFloors: { ...floors, [fieldType]: updated },
      });
    },
    [floors, thresholds, onChange],
  );

  const removeFloor = useCallback(
    (fieldType: string) => {
      const { [fieldType]: _, ...rest } = floors;
      onChange({ ...thresholds, autoSkipFloors: rest });
    },
    [floors, thresholds, onChange],
  );

  const addFieldType = useCallback(() => {
    if (!newFieldType || floors[newFieldType]) return;
    updateFloor(newFieldType, {
      floor: 0.95,
      mode: "fixed",
      hardMinimum: 0.88,
      initialFloor: 0.95,
    });
    setNewFieldType("");
  }, [newFieldType, floors, updateFloor]);

  const entries = Object.entries(floors);

  return (
    <div className="floor-config">
      <h3 className="rb-section-title">Per-Field-Type Confidence Floors</h3>
      <p className="rb-hint">
        Each floor is the minimum OCR confidence at/above which fields of that
        type are auto-verified; below it they stop for review. Adaptive mode
        lets floors drift based on calibration data.
      </p>
      <p className="rb-hint">
        Changes save when you click out of the input (Tab or Enter also
        commit).
      </p>

      {entries.length === 0 && (
        <p className="rb-hint">
          No custom floors configured. All fields use the global default (0.95).
        </p>
      )}

      {entries.map(([fieldType, config]) => (
        <div key={fieldType} className="floor-row">
          <span className="floor-field-type">{fieldType}</span>

          <label className="floor-input-group">
            <span>Floor</span>
            <NumberInputOnBlur
              value={config.floor}
              min={0.5}
              max={1}
              step={0.01}
              ariaLabel={`Floor for ${fieldType}`}
              onCommit={(floor) => updateFloor(fieldType, { floor })}
            />
          </label>

          <label className="floor-input-group">
            <span>Mode</span>
            <select
              value={config.mode}
              onChange={(e) =>
                updateFloor(fieldType, {
                  mode: e.target.value as "adaptive" | "fixed",
                })
              }
            >
              <option value="fixed">Fixed</option>
              <option value="adaptive">Adaptive</option>
            </select>
          </label>

          {config.mode === "adaptive" && (
            <label className="floor-input-group">
              <span>Hard Min</span>
              <NumberInputOnBlur
                value={config.hardMinimum ?? 0.88}
                min={0.5}
                max={1}
                step={0.01}
                ariaLabel={`Hard minimum for ${fieldType}`}
                onCommit={(hardMinimum) =>
                  updateFloor(fieldType, { hardMinimum })
                }
              />
            </label>
          )}

          <button
            className="floor-remove"
            onClick={() => removeFloor(fieldType)}
            title="Remove floor"
          >
            ×
          </button>
        </div>
      ))}

      <div className="floor-add">
        <select
          value={newFieldType}
          onChange={(e) => setNewFieldType(e.target.value)}
        >
          <option value="">Add field type...</option>
          {COMMON_FIELD_TYPES.filter((ft) => !floors[ft]).map((ft) => (
            <option key={ft} value={ft}>
              {ft}
            </option>
          ))}
          <option value="default">default (fallback)</option>
        </select>
        <button onClick={addFieldType} disabled={!newFieldType}>
          Add
        </button>
      </div>
    </div>
  );
}

/**
 * Phase 1.5 WU-4 (R-127, 2026-05-23) — `FocusModeThresholdEditor` was
 * REMOVED. The doc-level Focus Attention Threshold is no longer load-
 * bearing: smart-skip is the default keying mode and the per-type floors
 * (`FloorConfigEditor` above) fully determine which fields stop. Setting a
 * type's floor to 1.00 covers the "key everything" case the doc-level
 * threshold used to. See `docs/walkthroughs/2026-05-16-fix-verification-
 * findings.md` R-127 for the full decision trail.
 *
 * Operator-level preferences `autoFocusMode` / `defaultFocusMode` in
 * `packages/shared/src/types/preferences.ts` are a SEPARATE concept
 * (per-operator UI toggle) and remain unchanged.
 */
